// Settings page with support for multi-account invitations
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { User, Calendar, Bell, Palette, LogOut, Plus, Trash2, X, Users, RefreshCw, Link2, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, CheckCircle, List, Eye, EyeOff, Monitor, Sun, Moon, Mail, Clock, Settings } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useFamilyMembers, useFamily, useUpdateFamily, useCalendarSources, useGoogleCalendars, useMicrosoftCalendars, useAddCalendarSource, useSyncCalendarSource, useDeleteCalendarSource, useKeepStatus, useConnectKeep, useConnectKeepOAuth, useDisconnectKeep, useKeepLists, useKeepSources, useEnableKeepSync, useTriggerKeepSync, useDisableKeepSync, useNotificationSettings, useUpdateNotificationSettings, usePushSubscribe, usePushTest, usePushStatus, useDisplaySettings, useUpdateDisplaySettings, useDailyBriefing, useSendDailyBriefing, useLists, useDeleteList, useInvitations, useUpdateMemberKeywords } from '../api/hooks';
import { isPushSupported, getPushPermission, requestPushPermission, subscribeToPush, unsubscribeFromPush, subscriptionToJSON, isPushEnabled as checkPushEnabled } from '../lib/push';
import { api } from '../api/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { analytics } from '../lib/analytics';
import { useToast } from '../context/ToastContext';
import { AddressAutocomplete, AddressData } from '../components/AddressAutocomplete';
import { TravelTimePreview } from '../components/TravelTimePreview';
import { InviteMemberModal } from '../components/InviteMemberModal';
import { PairingCodeModal } from '../components/PairingCodeModal';
import { MemberCalendarActions } from '../components/MemberCalendarActions';
import { ConnectedAccountsStatus } from '../components/ConnectedAccountsStatus';
import { AddCompassModal } from '../components/AddCompassModal';
import { useIsPhone } from '../hooks/useMediaQuery';

const memberColors = [
  '#15a2a2', // Teal (primary)
  '#fe7434', // Orange (strong accent)
  '#1184a7', // Teal Dark (secondary)
  '#fea802', // Amber (highlights)
  '#ea515f', // Coral (warm accent)
  '#6fb1a0', // Sage (success)
  '#046dc8', // Blue (links)
  '#b4418e', // Magenta (member color)
  '#d94a8c', // Pink (member color)
  '#0450b4', // Navy (deep accent)
];

const timezones = [
  // Australia & Pacific
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Pacific/Fiji', label: 'Fiji' },
  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  // Americas
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Phoenix', label: 'Phoenix (MST)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
  // Other
  { value: 'UTC', label: 'UTC' },
];

interface MemberFormData {
  name: string;
  type: 'adult' | 'teen' | 'child';
  color: string;
}

// Reminder time options in minutes
const reminderTimeOptions = [
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
];

// Daily briefing time options (every 30 minutes)
const briefingTimeOptions = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = (i % 2) * 30;
  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  const display = new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  return { value: time, label: display };
});

// Hour options for calendar display range
const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: new Date(2000, 0, 1, i).toLocaleTimeString([], { hour: 'numeric', hour12: true }),
}));

// ============================================
// Tab definitions
// ============================================
type SettingsTab = 'family' | 'display' | 'notifications' | 'integrations' | 'account';

const SETTINGS_TABS: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
  { id: 'family', label: 'Family', icon: <Users className="w-4 h-4" /> },
  { id: 'display', label: 'Display', icon: <Palette className="w-4 h-4" /> },
  { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
  { id: 'integrations', label: 'Integrations', icon: <Link2 className="w-4 h-4" /> },
  { id: 'account', label: 'Account', icon: <Settings className="w-4 h-4" /> },
];

// ============================================
// Display Settings Section
// ============================================
function DisplaySettingsSection() {
  const { data: displaySettings, isLoading } = useDisplaySettings();
  const updateSettings = useUpdateDisplaySettings();
  const [localTheme, setLocalTheme] = useState<'light' | 'dark'>(
    () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light')
  );

  const handleTimeFormatChange = (value: '12h' | '24h') => {
    updateSettings.mutate({ timeFormat: value });
    analytics.featureUsed('display_settings_changed', { setting: 'time_format', value });
  };

  const handleWeekStartsOnChange = (value: 0 | 1) => {
    updateSettings.mutate({ weekStartsOn: value });
    analytics.featureUsed('display_settings_changed', { setting: 'week_starts_on', value });
  };

  const handleDefaultViewChange = (value: 'day' | 'week' | 'month') => {
    updateSettings.mutate({ defaultView: value });
    analytics.featureUsed('display_settings_changed', { setting: 'default_view', value });
  };

  const handleStartHourChange = (value: number) => {
    updateSettings.mutate({ startHour: value });
    analytics.featureUsed('display_settings_changed', { setting: 'start_hour', value });
  };

  const handleEndHourChange = (value: number) => {
    updateSettings.mutate({ endHour: value });
    analytics.featureUsed('display_settings_changed', { setting: 'end_hour', value });
  };

  const handleSchoolStartHourChange = (value: number) => {
    updateSettings.mutate({ schoolStartHour: value });
    analytics.featureUsed('display_settings_changed', { setting: 'school_start_hour', value });
  };

  const handleSchoolEndHourChange = (value: number) => {
    updateSettings.mutate({ schoolEndHour: value });
    analytics.featureUsed('display_settings_changed', { setting: 'school_end_hour', value });
  };

  const handleShowWeekendsToggle = () => {
    const newValue = !displaySettings?.showWeekends;
    updateSettings.mutate({ showWeekends: newValue });
    analytics.featureUsed('display_settings_changed', { setting: 'show_weekends', value: newValue });
  };

  const handleThemeChange = (value: 'light' | 'dark') => {
    setLocalTheme(value);
    updateSettings.mutate({ theme: value });
    analytics.featureUsed('display_settings_changed', { setting: 'theme', value });
    if (value === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  if (isLoading) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <Palette className="w-5 h-5" />
          Display Settings
        </h2>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 animate-spin text-text-muted" />
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
        <Palette className="w-5 h-5" />
        Display Settings
      </h2>
      <div className="space-y-4">
        {/* Theme */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Theme</div>
            <div className="text-sm text-text-secondary">Choose light or dark mode</div>
          </div>
          <div className="flex items-center gap-1 p-1 bg-bg-surface-hover rounded-lg border border-border-light">
            <button
              onClick={() => handleThemeChange('light')}
              disabled={updateSettings.isPending}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                localTheme === 'light'
                  ? 'bg-teal text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Sun className="w-4 h-4" />
              Light
            </button>
            <button
              onClick={() => handleThemeChange('dark')}
              disabled={updateSettings.isPending}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                localTheme === 'dark'
                  ? 'bg-teal text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Moon className="w-4 h-4" />
              Dark
            </button>
          </div>
        </div>

        {/* Default Calendar View */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Default View</div>
            <div className="text-sm text-text-secondary">Calendar view on startup</div>
          </div>
          <select
            className="px-3 py-2 border border-border-default rounded-lg bg-white dark:bg-bg-surface"
            value={displaySettings?.defaultView || 'day'}
            onChange={(e) => handleDefaultViewChange(e.target.value as 'day' | 'week' | 'month')}
            disabled={updateSettings.isPending}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>

        {/* Time Format */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Time Format</div>
            <div className="text-sm text-text-secondary">12-hour or 24-hour</div>
          </div>
          <select
            className="px-3 py-2 border border-border-default rounded-lg bg-white"
            value={displaySettings?.timeFormat || '12h'}
            onChange={(e) => handleTimeFormatChange(e.target.value as '12h' | '24h')}
            disabled={updateSettings.isPending}
          >
            <option value="12h">12-hour</option>
            <option value="24h">24-hour</option>
          </select>
        </div>

        {/* Week Starts On */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Week Starts On</div>
            <div className="text-sm text-text-secondary">First day of the week</div>
          </div>
          <select
            className="px-3 py-2 border border-border-default rounded-lg bg-white"
            value={displaySettings?.weekStartsOn ?? 0}
            onChange={(e) => handleWeekStartsOnChange(Number(e.target.value) as 0 | 1)}
            disabled={updateSettings.isPending}
          >
            <option value={0}>Sunday</option>
            <option value={1}>Monday</option>
          </select>
        </div>

        {/* Calendar Hours Range */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Day View Hours</div>
            <div className="text-sm text-text-secondary">Time range shown in calendar</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="px-3 py-2 border border-border-default rounded-lg bg-white text-sm"
              value={displaySettings?.startHour ?? 6}
              onChange={(e) => handleStartHourChange(Number(e.target.value))}
              disabled={updateSettings.isPending}
            >
              {hourOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-text-secondary">to</span>
            <select
              className="px-3 py-2 border border-border-default rounded-lg bg-white text-sm"
              value={displaySettings?.endHour ?? 21}
              onChange={(e) => handleEndHourChange(Number(e.target.value))}
              disabled={updateSettings.isPending}
            >
              {hourOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* School Day Hours */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">School Day Hours</div>
            <div className="text-sm text-text-secondary">Time range shown in school view</div>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="px-3 py-2 border border-border-default rounded-lg bg-white text-sm"
              value={displaySettings?.schoolStartHour ?? 7}
              onChange={(e) => handleSchoolStartHourChange(Number(e.target.value))}
              disabled={updateSettings.isPending}
            >
              {hourOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="text-text-secondary">to</span>
            <select
              className="px-3 py-2 border border-border-default rounded-lg bg-white text-sm"
              value={displaySettings?.schoolEndHour ?? 19}
              onChange={(e) => handleSchoolEndHourChange(Number(e.target.value))}
              disabled={updateSettings.isPending}
            >
              {hourOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Show Weekends */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Show Weekends</div>
            <div className="text-sm text-text-secondary">Display Saturday and Sunday</div>
          </div>
          <button
            onClick={handleShowWeekendsToggle}
            disabled={updateSettings.isPending}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              displaySettings?.showWeekends !== false ? 'bg-soft-cyan' : 'bg-gray-200'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                displaySettings?.showWeekends !== false ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// Kiosk Settings Section
// ============================================
function KioskSettingsSection() {
  const { data: displaySettings, isLoading } = useDisplaySettings();
  const updateSettings = useUpdateDisplaySettings();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [showPairingModal, setShowPairingModal] = useState(false);

  const handleKioskModeToggle = () => {
    const newValue = !displaySettings?.kioskMode;
    updateSettings.mutate({ kioskMode: newValue });
    analytics.featureUsed('display_settings_changed', { setting: 'kiosk_mode', value: newValue });

    if (newValue) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  if (isLoading) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <Monitor className="w-5 h-5" />
          Kiosk Settings
        </h2>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 animate-spin text-text-muted" />
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
        <Monitor className="w-5 h-5" />
        Kiosk Settings
      </h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Kiosk Mode</div>
            <div className="text-sm text-text-secondary">Full screen, always-on display</div>
          </div>
          <button
            onClick={handleKioskModeToggle}
            disabled={updateSettings.isPending}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              displaySettings?.kioskMode ? 'bg-soft-cyan' : 'bg-gray-200'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                displaySettings?.kioskMode ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>

        {isAdmin && (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-text-primary">Pair Kiosk Device</div>
              <div className="text-sm text-text-secondary">Generate code for kiosk sign-in</div>
            </div>
            <button
              onClick={() => setShowPairingModal(true)}
              className="px-4 py-2 bg-soft-cyan text-white rounded-lg font-medium hover:bg-opacity-90"
            >
              Generate Code
            </button>
          </div>
        )}

        {showPairingModal && (
          <PairingCodeModal onClose={() => setShowPairingModal(false)} />
        )}
      </div>
    </section>
  );
}

// ============================================
// Notifications Section
// ============================================
function NotificationsSection() {
  const { data: settings, isLoading: settingsLoading } = useNotificationSettings();
  const { data: pushStatus } = usePushStatus();
  const updateSettings = useUpdateNotificationSettings();
  const pushSubscribe = usePushSubscribe();
  const pushTest = usePushTest();
  const { data: briefing, isLoading: briefingLoading, refetch: refetchBriefing } = useDailyBriefing();
  const sendBriefing = useSendDailyBriefing();

  const [pushSupported] = useState(() => isPushSupported());
  const [pushPermission, setPushPermission] = useState<NotificationPermission | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isProcessingPush, setIsProcessingPush] = useState(false);
  const [showBriefingPreview, setShowBriefingPreview] = useState(false);

  // Check push permission and subscription status
  useEffect(() => {
    if (pushSupported) {
      setPushPermission(getPushPermission());
      checkPushEnabled().then(setIsSubscribed);
    }
  }, [pushSupported]);

  const handlePushToggle = async () => {
    if (isProcessingPush) return;
    setIsProcessingPush(true);

    try {
      if (isSubscribed) {
        // Unsubscribe
        const unsubscribed = await unsubscribeFromPush();
        if (unsubscribed) {
          setIsSubscribed(false);
          analytics.pushNotificationsDisabled();
        }
      } else {
        // Subscribe
        const permission = await requestPushPermission();
        setPushPermission(permission);

        if (permission === 'granted') {
          const subscription = await subscribeToPush();
          if (subscription) {
            const subData = subscriptionToJSON(subscription);
            if (subData) {
              await pushSubscribe.mutateAsync(subData);
              setIsSubscribed(true);
              analytics.pushNotificationsEnabled();
            }
          }
        }
      }
    } catch (error) {
      console.error('Push toggle error:', error);
    } finally {
      setIsProcessingPush(false);
    }
  };

  const handleTestNotification = () => {
    pushTest.mutate();
  };

  const handleUpdateSetting = <K extends keyof NonNullable<typeof settings>>(
    key: K,
    value: NonNullable<typeof settings>[K]
  ) => {
    updateSettings.mutate({ [key]: value });
    analytics.notificationSettingsChanged(key, value);
  };

  const handleReminderToggle = (minutes: number) => {
    if (!settings) return;
    const current = settings.eventReminderMinutes || [];
    const updated = current.includes(minutes)
      ? current.filter((m) => m !== minutes)
      : [...current, minutes].sort((a, b) => a - b);
    handleUpdateSetting('eventReminderMinutes', updated);
  };

  if (settingsLoading) {
    return (
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5" />
          Notifications
        </h2>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
        <Bell className="w-5 h-5" />
        Notifications
      </h2>
      <div className="space-y-6">
        {/* Push Notifications */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-text-primary">Push Notifications</div>
              <div className="text-sm text-text-secondary">
                {!pushSupported
                  ? 'Not supported in this browser'
                  : pushPermission === 'denied'
                    ? 'Blocked - enable in browser settings'
                    : !pushStatus?.enabled
                      ? 'Not configured on server'
                      : isSubscribed
                        ? 'Enabled on this device'
                        : 'Receive alerts on this device'}
              </div>
            </div>
            <button
              onClick={handlePushToggle}
              disabled={!pushSupported || pushPermission === 'denied' || !pushStatus?.enabled || isProcessingPush}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                isSubscribed ? 'bg-soft-cyan' : 'bg-gray-200'
              } ${(!pushSupported || pushPermission === 'denied' || !pushStatus?.enabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  isSubscribed ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Test Notification Button */}
          {isSubscribed && (
            <button
              onClick={handleTestNotification}
              disabled={pushTest.isPending}
              className="text-sm text-soft-cyan hover:underline"
            >
              {pushTest.isPending ? 'Sending...' : 'Send test notification'}
            </button>
          )}
        </div>

        {/* Event Reminders */}
        <div className="space-y-3">
          <div>
            <div className="font-medium text-text-primary">Event Reminders</div>
            <div className="text-sm text-text-secondary">When to remind you before events</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {reminderTimeOptions.map((option) => {
              const isSelected = settings?.eventReminderMinutes?.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleReminderToggle(option.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-soft-cyan text-white border-soft-cyan'
                      : 'bg-bg-surface text-text-primary border-border-default hover:bg-bg-surface-hover'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Travel Time Alerts */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Travel Time Alerts</div>
            <div className="text-sm text-text-secondary">
              Include travel time in event reminders
            </div>
          </div>
          <button
            onClick={() => handleUpdateSetting('travelTimeAlerts', !settings?.travelTimeAlerts)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              settings?.travelTimeAlerts ? 'bg-soft-cyan' : 'bg-gray-200'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                settings?.travelTimeAlerts ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>

        {/* Chore Reminders */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Chore Reminders</div>
            <div className="text-sm text-text-secondary">
              Get notified when chores are due
            </div>
          </div>
          <button
            onClick={() => handleUpdateSetting('choreReminders', !settings?.choreReminders)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              settings?.choreReminders ? 'bg-soft-cyan' : 'bg-gray-200'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                settings?.choreReminders ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>

        {/* Daily Briefing */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-text-primary">Daily Briefing</div>
              <div className="text-sm text-text-secondary">
                Summary of today's events and chores
              </div>
            </div>
            <button
              onClick={() => handleUpdateSetting('dailyBriefingEnabled', !settings?.dailyBriefingEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings?.dailyBriefingEnabled ? 'bg-soft-cyan' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings?.dailyBriefingEnabled ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {settings?.dailyBriefingEnabled && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-secondary">Send at:</span>
                <select
                  value={settings.dailyBriefingTime || '07:30'}
                  onChange={(e) => handleUpdateSetting('dailyBriefingTime', e.target.value)}
                  className="px-3 py-1.5 border border-border-default rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                >
                  {briefingTimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowBriefingPreview(!showBriefingPreview);
                    if (!showBriefingPreview) {
                      refetchBriefing();
                    }
                  }}
                  className="text-sm text-soft-cyan hover:underline"
                >
                  {showBriefingPreview ? 'Hide preview' : 'Preview today\'s briefing'}
                </button>
                {isSubscribed && (
                  <>
                    <span className="text-text-muted">•</span>
                    <button
                      onClick={() => {
                        sendBriefing.mutate(undefined, {
                          onSuccess: (data) => {
                            analytics.featureUsed('daily_briefing_sent_manually', { sent: data.sent });
                          },
                        });
                      }}
                      disabled={sendBriefing.isPending}
                      className="text-sm text-soft-cyan hover:underline"
                    >
                      {sendBriefing.isPending ? 'Sending...' : 'Send now'}
                    </button>
                  </>
                )}
              </div>

              {/* Briefing Preview */}
              {showBriefingPreview && (
                <div className="p-3 bg-bg-surface-hover rounded-lg border border-border-light">
                  {briefingLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-4 h-4 animate-spin text-text-muted" />
                    </div>
                  ) : briefing?.content ? (
                    <div className="space-y-2">
                      <div className="text-xs text-text-muted">
                        {briefing.events} event{briefing.events !== 1 ? 's' : ''}, {briefing.chores} chore{briefing.chores !== 1 ? 's' : ''} today
                      </div>
                      <pre className="text-sm text-text-primary whitespace-pre-wrap font-sans">
                        {briefing.content}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-sm text-text-muted text-center py-2">
                      No events or chores scheduled for today
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Audio Alerts */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-text-primary">Audio Alerts</div>
            <div className="text-sm text-text-secondary">
              Play a sound with notifications
            </div>
          </div>
          <button
            onClick={() => handleUpdateSetting('audioAlerts', !settings?.audioAlerts)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              settings?.audioAlerts ? 'bg-soft-cyan' : 'bg-gray-200'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                settings?.audioAlerts ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// Main Settings Page
// ============================================
export function SettingsPage() {
  const { user, logout } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { data: members, isLoading: membersLoading } = useFamilyMembers();
  const { data: family, isLoading: familyLoading } = useFamily();
  const { data: invitations } = useInvitations();
  const updateFamily = useUpdateFamily();

  // Tab state from URL
  const activeTab = (searchParams.get('tab') as SettingsTab) || 'family';
  const setActiveTab = (tab: SettingsTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Family settings state
  const [familyName, setFamilyName] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [timezone, setTimezone] = useState('');

  // Initialize family settings from fetched data
  useEffect(() => {
    if (family) {
      setFamilyName(family.name);
      setHomeAddress(family.homeAddress || '');
      setTimezone(family.timezone);
    }
  }, [family]);

  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<{ id: string; name: string; type: string; color: string } | null>(null);
  const [memberForm, setMemberForm] = useState<MemberFormData>({ name: '', type: 'adult', color: memberColors[0] ?? '#15a2a2' });

  // Calendar source management state
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [addSourceMemberId, setAddSourceMemberId] = useState<string | null>(null);
  const [addSourceType, setAddSourceType] = useState<'google' | 'microsoft' | 'ics' | 'compass' | null>(null);
  const [icsUrl, setIcsUrl] = useState('');
  const [icsName, setIcsName] = useState('');

  // Calendar source hooks
  const { data: calendarSources, isLoading: sourcesLoading } = useCalendarSources(expandedMemberId || '');
  // Only fetch calendars when the modal is open for the correct type
  const { data: googleCalendars, isLoading: googleLoading } = useGoogleCalendars(
    addSourceType === 'google' ? addSourceMemberId : null
  );
  const { data: microsoftCalendars, isLoading: microsoftLoading } = useMicrosoftCalendars(
    addSourceType === 'microsoft' ? addSourceMemberId : null
  );
  const addCalendarSource = useAddCalendarSource();
  const syncCalendarSource = useSyncCalendarSource();
  const deleteCalendarSource = useDeleteCalendarSource();
  const [syncingSourceId, setSyncingSourceId] = useState<string | null>(null);
  const [syncMenuOpenId, setSyncMenuOpenId] = useState<string | null>(null);

  // Search keywords state (for child/teen event matching)
  const [newKeywordByMember, setNewKeywordByMember] = useState<Record<string, string>>({});
  const updateMemberKeywords = useUpdateMemberKeywords();

  // Google Keep state
  const [showKeepConnectModal, setShowKeepConnectModal] = useState(false);
  const [showKeepListsModal, setShowKeepListsModal] = useState(false);
  const [keepEmail, setKeepEmail] = useState('');
  const [keepPassword, setKeepPassword] = useState('');
  const [showKeepPassword, setShowKeepPassword] = useState(false);
  const [keepConnectError, setKeepConnectError] = useState<string | null>(null);
  const [keepConnectMethod, setKeepConnectMethod] = useState<'app-password' | 'oauth'>('oauth');
  const [keepOAuthToken, setKeepOAuthToken] = useState('');

  // Lists management state
  const [showDeleteListModal, setShowDeleteListModal] = useState(false);
  const [listToDelete, setListToDelete] = useState<{ id: string; name: string } | null>(null);

  // Invite member state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMemberId, setInviteMemberId] = useState<string | null>(null);
  const [inviteMemberName, setInviteMemberName] = useState<string>('');

  // Google Keep hooks
  const { data: keepStatus, isLoading: keepStatusLoading } = useKeepStatus();
  const { data: keepLists, isLoading: keepListsLoading } = useKeepLists();
  const { data: keepSources } = useKeepSources();
  const connectKeep = useConnectKeep();
  const connectKeepOAuth = useConnectKeepOAuth();
  const disconnectKeep = useDisconnectKeep();
  const enableKeepSync = useEnableKeepSync();
  const triggerKeepSync = useTriggerKeepSync();
  const disableKeepSync = useDisableKeepSync();

  // Lists hooks
  const { data: lists, isLoading: listsListLoading } = useLists();
  const deleteList = useDeleteList();

  // Display settings hooks (for default list in Manage Lists section)
  const { data: displaySettings } = useDisplaySettings();
  const updateDisplaySettings = useUpdateDisplaySettings();

  // Handle OAuth return - restore UI state after connecting a calendar provider
  useEffect(() => {
    const memberId = searchParams.get('memberId');
    const sourceType = searchParams.get('sourceType') as 'google' | 'microsoft' | null;
    const openModal = searchParams.get('openModal');

    if (memberId && sourceType && openModal === 'true') {
      // Verify member still exists before opening modal
      const member = members?.find(m => m.id === memberId);
      if (member) {
        // For Google/Microsoft, verify member has a userId (connected account)
        if ((sourceType === 'google' || sourceType === 'microsoft') && !member.userId) {
          // Member needs to connect account first - don't open modal
          console.warn(`Cannot open ${sourceType} calendar modal: member has no connected account`);
        } else {
          setExpandedMemberId(memberId);
          setAddSourceMemberId(memberId);
          setAddSourceType(sourceType);
          setShowAddSourceModal(true);
        }
      }
      // Clear URL params to prevent re-triggering (keep tab param)
      setSearchParams({ tab: 'family' }, { replace: true });
    }
  }, [searchParams, members, setSearchParams]);

  // Close sync dropdown when clicking outside
  useEffect(() => {
    if (!syncMenuOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-sync-menu]')) {
        setSyncMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [syncMenuOpenId]);

  const createMember = useMutation({
    mutationFn: (data: MemberFormData) => api.post('/members', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setShowMemberModal(false);
      setMemberForm({ name: '', type: 'adult', color: memberColors[0] ?? '#15a2a2' });
    },
    onError: (error: Error) => {
      alert(`Failed to add member: ${error.message}`);
    },
  });

  const updateMember = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<MemberFormData>) => api.patch(`/members/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setEditingMember(null);
      setShowMemberModal(false);
      setMemberForm({ name: '', type: 'adult', color: memberColors[0] ?? '#15a2a2' });
    },
  });

  const deleteMember = useMutation({
    mutationFn: (id: string) => api.delete(`/members/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error: Error) => {
      alert(`Failed to delete member: ${error.message}`);
    },
  });

  const handleLogout = async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST' });
    logout();
    window.location.href = '/login';
  };

  const handleOpenCreateMember = () => {
    setEditingMember(null);
    const colorIndex = (members?.length ?? 0) % memberColors.length;
    setMemberForm({ name: '', type: 'adult', color: memberColors[colorIndex] ?? '#15a2a2' });
    setShowMemberModal(true);
  };

  const handleOpenEditMember = (member: { id: string; name: string; type: string; color: string }) => {
    setEditingMember(member);
    setMemberForm({ name: member.name, type: member.type as 'adult' | 'teen' | 'child', color: member.color });
    setShowMemberModal(true);
  };

  const handleSubmitMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberForm.name.trim()) return;

    if (editingMember) {
      updateMember.mutate({ id: editingMember.id, ...memberForm });
    } else {
      createMember.mutate(memberForm);
    }
  };

  // Find pending invitation for a member
  const getPendingInvitation = (memberId: string) => {
    return invitations?.find(
      (inv) => inv.memberId === memberId && inv.status === 'pending'
    );
  };

  // Opens the add source modal - the modal handles loading state and OAuth redirect if needed
  // Also checks if member has a user account - if not, prompts to send invite first
  const handleAddSourceClick = (memberId: string, type: 'google' | 'microsoft' | 'ics' | 'compass') => {
    const member = members?.find(m => m.id === memberId);

    // For OAuth-based calendars (Google/Microsoft), check if member has a user account
    if ((type === 'google' || type === 'microsoft') && member && !member.userId) {
      // Member has no user account - prompt to send invite
      setInviteMemberId(memberId);
      setInviteMemberName(member.name);
      setShowInviteModal(true);
      return;
    }

    // Open modal - it will fetch calendars and show loading state, then either
    // display available calendars or a "Connect" button if none found
    handleOpenAddSource(memberId, type);
  };

  const handleOpenAddSource = (memberId: string, type: 'google' | 'microsoft' | 'ics' | 'compass') => {
    setAddSourceMemberId(memberId);
    setAddSourceType(type);
    setIcsUrl('');
    setIcsName('');
    setShowAddSourceModal(true);
  };

  const handleAddGoogleCalendar = (calendar: { id: string; name: string }) => {
    if (!addSourceMemberId) return;
    addCalendarSource.mutate({
      memberId: addSourceMemberId,
      type: 'google',
      name: calendar.name,
      externalId: calendar.id,
    }, {
      onSuccess: () => {
        setShowAddSourceModal(false);
        setAddSourceType(null);
      },
    });
  };

  const handleAddMicrosoftCalendar = (calendar: { id: string; name: string }) => {
    if (!addSourceMemberId) return;
    addCalendarSource.mutate({
      memberId: addSourceMemberId,
      type: 'microsoft',
      name: calendar.name,
      externalId: calendar.id,
    }, {
      onSuccess: () => {
        setShowAddSourceModal(false);
        setAddSourceType(null);
      },
    });
  };

  const handleAddIcsSource = () => {
    if (!addSourceMemberId || !icsUrl.trim()) return;
    addCalendarSource.mutate({
      memberId: addSourceMemberId,
      type: 'ics',
      name: icsName.trim() || 'ICS Calendar',
      sourceUrl: icsUrl.trim(),
    }, {
      onSuccess: () => {
        setShowAddSourceModal(false);
        setAddSourceType(null);
        setIcsUrl('');
        setIcsName('');
      },
    });
  };

  const handleSyncSource = (sourceId: string, sourceName?: string, force = false) => {
    setSyncingSourceId(sourceId);
    syncCalendarSource.mutate({ sourceId, force }, {
      onSuccess: (data) => {
        const syncedCount = data?.syncedEvents ?? 0;
        const syncType = force ? 'Full sync' : 'Synced';
        toast.success(`${syncType}: ${syncedCount} events${sourceName ? ` from ${sourceName}` : ''}`);
        setSyncingSourceId(null);
      },
      onError: (error: Error) => {
        toast.error(`Sync failed: ${error.message || 'Unknown error'}`);
        setSyncingSourceId(null);
      },
    });
  };

  const handleDeleteSource = (sourceId: string, sourceName: string) => {
    if (confirm(`Remove "${sourceName}" calendar source?`)) {
      deleteCalendarSource.mutate(sourceId);
    }
  };

  // Google Keep handlers
  const handleConnectKeep = (e: React.FormEvent) => {
    e.preventDefault();
    setKeepConnectError(null);

    connectKeep.mutate(
      { email: keepEmail, password: keepPassword },
      {
        onSuccess: () => {
          setShowKeepConnectModal(false);
          setKeepEmail('');
          setKeepPassword('');
        },
        onError: (error: Error) => {
          setKeepConnectError(error.message || 'Failed to connect to Google Keep');
        },
      }
    );
  };

  const handleConnectKeepOAuth = (e: React.FormEvent) => {
    e.preventDefault();
    setKeepConnectError(null);

    connectKeepOAuth.mutate(
      { email: keepEmail, oauthToken: keepOAuthToken },
      {
        onSuccess: () => {
          setShowKeepConnectModal(false);
          setKeepEmail('');
          setKeepOAuthToken('');
        },
        onError: (error: Error) => {
          setKeepConnectError(error.message || 'Failed to connect to Google Keep');
        },
      }
    );
  };

  const handleDisconnectKeep = () => {
    if (confirm('Disconnect Google Keep? This will stop syncing all Keep lists.')) {
      disconnectKeep.mutate();
    }
  };

  const handleImportKeepList = (keepListId: string) => {
    analytics.keepListImported(keepListId);
    enableKeepSync.mutate(
      { keepListId, syncDirection: 'bidirectional' },
      {
        onSuccess: () => {
          // List imported successfully
        },
      }
    );
  };

  const handleSyncKeepSource = (sourceId: string) => {
    analytics.keepSyncTriggered(sourceId);
    triggerKeepSync.mutate(sourceId);
  };

  const handleRemoveKeepSync = (sourceId: string, listName: string) => {
    if (confirm(`Stop syncing "${listName}"? The local list will remain but won't sync with Keep anymore.`)) {
      disableKeepSync.mutate(sourceId);
    }
  };

  // List deletion handlers
  const handleOpenDeleteListModal = (listId: string, listName: string) => {
    setListToDelete({ id: listId, name: listName });
    setShowDeleteListModal(true);
  };

  const handleConfirmDeleteList = () => {
    if (listToDelete) {
      deleteList.mutate(listToDelete.id, {
        onSuccess: () => {
          toast.success(`"${listToDelete.name}" deleted`);
          setShowDeleteListModal(false);
          setListToDelete(null);
          analytics.featureUsed('list_deleted', { list_name: listToDelete.name });
        },
        onError: (error: Error) => {
          toast.error(`Failed to delete list: ${error.message}`);
        },
      });
    }
  };

  // ============================================
  // Tab content renderers
  // ============================================

  const renderFamilyTab = () => (
    <div className="space-y-6">
      {/* Family Members */}
      <section className="card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Users className="w-5 h-5" />
            Family Members
          </h2>
          <button onClick={handleOpenCreateMember} className="btn btn-primary text-sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Member
          </button>
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Click a member to manage their calendar connections.
        </p>

        {membersLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        ) : !members || members.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-text-muted mx-auto mb-2" />
            <p className="text-text-secondary">No family members yet</p>
            <button onClick={handleOpenCreateMember} className="text-soft-cyan hover:underline mt-2">
              Add your first member
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const isExpanded = expandedMemberId === member.id;
              return (
                <div key={member.id} className="border border-border-light rounded-lg">
                  {/* Member Header */}
                  <div className="flex items-center gap-3 p-3 hover:bg-bg-surface-hover transition-colors">
                    <button
                      onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                      className="text-text-muted hover:text-text-primary transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer"
                      style={{ backgroundColor: member.color }}
                      onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                    >
                      {member.name.substring(0, 2)}
                    </div>
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                    >
                      <div className="font-medium text-text-primary">{member.name}</div>
                      <div className="text-sm text-text-secondary capitalize">
                        {member.type}
                        {isExpanded && calendarSources && (
                          <span className="text-text-muted ml-1">
                            · {calendarSources.length} calendar{calendarSources.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenEditMember(member)}
                      className="text-sm text-soft-cyan hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove ${member.name} from the family?`)) {
                          deleteMember.mutate(member.id);
                        }
                      }}
                      className="text-text-muted hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Expanded Content: Calendar Sources */}
                  {isExpanded && (
                    <div className="border-t border-border-light bg-bg-surface-hover p-3">
                      {sourcesLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="w-5 h-5 animate-spin text-text-muted" />
                        </div>
                      ) : (
                        <>
                          {/* Connected Accounts Status */}
                          {member.userId && (
                            <ConnectedAccountsStatus memberId={member.id} compact={true} />
                          )}

                          {/* Connected Sources */}
                          {calendarSources && calendarSources.length > 0 ? (
                            <div className="space-y-2 mb-3">
                              {calendarSources.map((source) => (
                                <div
                                  key={source.id}
                                  className="flex items-center gap-3 p-2 bg-bg-surface rounded-lg border border-border-light relative"
                                >
                                  {/* Source Icon */}
                                  <div className="w-8 h-8 rounded flex items-center justify-center bg-gray-100">
                                    {source.type === 'google' && (
                                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                      </svg>
                                    )}
                                    {source.type === 'microsoft' && (
                                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#F25022" d="M1 1h10v10H1z" />
                                        <path fill="#00A4EF" d="M1 13h10v10H1z" />
                                        <path fill="#7FBA00" d="M13 1h10v10H13z" />
                                        <path fill="#FFB900" d="M13 13h10v10H13z" />
                                      </svg>
                                    )}
                                    {source.type === 'ics' && (
                                      <Link2 className="w-4 h-4 text-text-secondary" />
                                    )}
                                  </div>
                                  {/* Source Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-text-primary truncate">
                                      {source.name}
                                    </div>
                                    {source.accountEmail && (
                                      <div className="text-xs text-text-secondary truncate">
                                        {source.accountEmail}
                                      </div>
                                    )}
                                    <div className="text-xs text-text-muted">
                                      {source.lastSynced
                                        ? `Synced ${formatDistanceToNow(new Date(source.lastSynced), { addSuffix: true })}`
                                        : 'Never synced'}
                                    </div>
                                  </div>
                                  {/* Sync Actions */}
                                  <div className="flex items-center gap-1">
                                    {/* Sync Icon Button - triggers quick sync */}
                                    <button
                                      onClick={() => handleSyncSource(source.id, source.name, false)}
                                      disabled={syncingSourceId === source.id}
                                      className="btn-icon w-7 h-7 flex items-center justify-center"
                                      title={syncingSourceId === source.id ? 'Syncing...' : 'Quick Sync'}
                                    >
                                      <RefreshCw className={`w-4 h-4 ${syncingSourceId === source.id ? 'animate-spin' : ''}`} />
                                    </button>
                                    {/* Dropdown Menu Trigger */}
                                    <div className="relative" data-sync-menu>
                                      <button
                                        ref={(el) => {
                                          if (el && syncMenuOpenId === source.id) {
                                            const rect = el.getBoundingClientRect();
                                            const dropdown = document.getElementById(`sync-dropdown-${source.id}`);
                                            if (dropdown) {
                                              dropdown.style.top = `${rect.bottom + 4}px`;
                                              dropdown.style.right = `${window.innerWidth - rect.right}px`;
                                            }
                                          }
                                        }}
                                        onClick={() => setSyncMenuOpenId(syncMenuOpenId === source.id ? null : source.id)}
                                        disabled={syncingSourceId === source.id}
                                        className="btn-icon w-5 h-7 flex items-center justify-center"
                                        title="Sync options"
                                      >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                      </button>
                                      {syncMenuOpenId === source.id && createPortal(
                                        <div
                                          id={`sync-dropdown-${source.id}`}
                                          className="fixed bg-bg-surface border border-border-light rounded-lg shadow-lg z-[9999] py-1 min-w-[100px]"
                                          style={{ right: '0px', top: '0px' }}
                                        >
                                          <button
                                            onClick={() => {
                                              setSyncMenuOpenId(null);
                                              handleSyncSource(source.id, source.name, false);
                                            }}
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-bg-surface-hover"
                                          >
                                            Quick Sync
                                          </button>
                                          <button
                                            onClick={() => {
                                              setSyncMenuOpenId(null);
                                              handleSyncSource(source.id, source.name, true);
                                            }}
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-bg-surface-hover"
                                          >
                                            Full Sync
                                          </button>
                                        </div>,
                                        document.body
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteSource(source.id, source.name)}
                                    className="btn-icon w-7 h-7 text-red-500 hover:text-red-600"
                                    title="Remove source"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-text-muted text-center py-2 mb-3">
                              No calendar sources connected
                            </p>
                          )}

                          {/* Contextual calendar actions based on member type and account status */}
                          {member.type === 'child' ? (
                            // Children: Admin adds ICS feeds and Compass directly
                            <div className="space-y-2">
                              <p className="text-xs text-text-secondary">
                                Add calendar feeds for {member.name}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => handleAddSourceClick(member.id, 'ics')}
                                  className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  ICS URL
                                </button>
                                <button
                                  onClick={() => handleAddSourceClick(member.id, 'compass')}
                                  className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Compass Education
                                </button>
                              </div>
                            </div>
                          ) : !member.userId ? (
                            // Adults/teens without account: Show invite flow
                            <div className="space-y-2">
                              {getPendingInvitation(member.id) ? (
                                // Pending invitation exists
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                                    <Clock className="w-3 h-3" />
                                    Invitation Pending
                                  </span>
                                  <button
                                    onClick={() => {
                                      setInviteMemberId(member.id);
                                      setInviteMemberName(member.name);
                                      setShowInviteModal(true);
                                    }}
                                    className="text-soft-cyan hover:underline"
                                  >
                                    Resend
                                  </button>
                                </div>
                              ) : (
                                // No invitation yet
                                <button
                                  onClick={() => {
                                    setInviteMemberId(member.id);
                                    setInviteMemberName(member.name);
                                    setShowInviteModal(true);
                                  }}
                                  className="text-xs px-3 py-1.5 rounded-lg bg-soft-cyan text-white hover:bg-opacity-90 transition-colors flex items-center gap-1"
                                >
                                  <Mail className="w-3 h-3" />
                                  Invite to Connect Calendars
                                </button>
                              )}
                              <p className="text-xs text-text-secondary">
                                {member.name} can connect their own Google/Microsoft calendars after joining.
                              </p>
                              <button
                                onClick={() => handleAddSourceClick(member.id, 'ics')}
                                className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                ICS URL
                              </button>
                            </div>
                          ) : member.userId === user?.id || isAdmin ? (
                            // User viewing their own member record OR admin viewing any member
                            <MemberCalendarActions
                              member={member}
                              currentUserId={user?.id}
                              isAdmin={isAdmin}
                              onAddSourceClick={handleAddSourceClick}
                              onInviteClick={(memberId, memberName) => {
                                setInviteMemberId(memberId);
                                setInviteMemberName(memberName);
                                setShowInviteModal(true);
                              }}
                            />
                          ) : (
                            // Adults/teens with account viewed by non-admin non-owner
                            <div className="space-y-2">
                              <p className="text-xs text-text-secondary">
                                {member.name} manages their own Google/Microsoft calendars.
                              </p>
                              <button
                                onClick={() => handleAddSourceClick(member.id, 'ics')}
                                className="text-xs px-3 py-1.5 rounded-lg border border-border-default hover:bg-white transition-colors flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                ICS URL
                              </button>
                            </div>
                          )}

                          {/* Search Keywords Section - Only for children and teens */}
                          {(member.type === 'child' || member.type === 'teen') && (
                            <div className="mt-3 pt-3 border-t border-border-light">
                              <div className="mb-2">
                                <div className="text-sm font-medium text-text-primary">Search Keywords</div>
                                <div className="text-xs text-text-secondary">
                                  Events with these words will appear in {member.name}&apos;s calendar
                                </div>
                              </div>

                              {/* Existing keywords as pills */}
                              {member.searchKeywords && member.searchKeywords.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {member.searchKeywords.map((keyword, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                                      style={{ backgroundColor: `${member.color}20`, color: member.color }}
                                    >
                                      {keyword}
                                      <button
                                        onClick={() => {
                                          const newKeywords = (member.searchKeywords || []).filter((k) => k !== keyword);
                                          updateMemberKeywords.mutate(
                                            { memberId: member.id, keywords: newKeywords },
                                            {
                                              onSuccess: () => {
                                                toast.success(`Removed "${keyword}"`);
                                                analytics.childKeywordRemoved(member.id, keyword);
                                              },
                                              onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove keyword'),
                                            }
                                          );
                                        }}
                                        className="hover:opacity-70"
                                        disabled={updateMemberKeywords.isPending}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Add keyword input */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder={`e.g., ${member.name}, ${member.name}'s Soccer`}
                                  value={newKeywordByMember[member.id] || ''}
                                  onChange={(e) => setNewKeywordByMember((prev) => ({ ...prev, [member.id]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newKeywordByMember[member.id]?.trim()) {
                                      const keyword = newKeywordByMember[member.id]!.trim();
                                      const newKeywords = [...(member.searchKeywords || []), keyword];
                                      updateMemberKeywords.mutate(
                                        { memberId: member.id, keywords: newKeywords },
                                        {
                                          onSuccess: () => {
                                            setNewKeywordByMember((prev) => ({ ...prev, [member.id]: '' }));
                                            toast.success(`Added "${keyword}"`);
                                            analytics.childKeywordAdded(member.id, keyword);
                                          },
                                          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to add keyword'),
                                        }
                                      );
                                    }
                                  }}
                                  className="flex-1 px-3 py-1.5 text-sm border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                                />
                                <button
                                  onClick={() => {
                                    const keyword = newKeywordByMember[member.id]?.trim();
                                    if (!keyword) return;
                                    const newKeywords = [...(member.searchKeywords || []), keyword];
                                    updateMemberKeywords.mutate(
                                      { memberId: member.id, keywords: newKeywords },
                                      {
                                        onSuccess: () => {
                                          setNewKeywordByMember((prev) => ({ ...prev, [member.id]: '' }));
                                          toast.success(`Added "${keyword}"`);
                                          analytics.childKeywordAdded(member.id, keyword);
                                        },
                                        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to add keyword'),
                                      }
                                    );
                                  }}
                                  disabled={!newKeywordByMember[member.id]?.trim() || updateMemberKeywords.isPending}
                                  className="px-3 py-1.5 text-xs bg-soft-cyan text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 transition-colors"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Family Settings */}
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <User className="w-5 h-5" />
          Family Settings
        </h2>
        {familyLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Family Name
              </label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                onBlur={() => {
                  if (familyName !== family?.name) {
                    updateFamily.mutate({ name: familyName });
                  }
                }}
                className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Home Address
              </label>
              <AddressAutocomplete
                value={homeAddress}
                isSaving={updateFamily.isPending}
                onChange={(data: AddressData | null) => {
                  console.log('[Settings] AddressAutocomplete onChange called:', data);
                  if (data) {
                    console.log('[Settings] Updating home address:', {
                      address: data.address,
                      placeId: data.placeId,
                      lat: data.lat,
                      lng: data.lng
                    });
                    setHomeAddress(data.address);
                    console.log('[Settings] Calling updateFamily.mutate...');
                    updateFamily.mutate(
                      {
                        homeAddress: data.address,
                        homeAddressPlaceId: data.placeId || null,
                        homeAddressLat: typeof data.lat === 'number' && data.lat !== 0 ? data.lat : null,
                        homeAddressLng: typeof data.lng === 'number' && data.lng !== 0 ? data.lng : null,
                      },
                      {
                        onSuccess: () => {
                          toast.success('Home address saved');
                        },
                        onError: (error) => {
                          console.error('Failed to save home address:', error);
                          toast.error('Failed to save home address');
                        },
                      }
                    );
                    analytics.featureUsed('home_address_updated', {
                      has_coordinates: !!(data.lat && data.lng && data.lat !== 0 && data.lng !== 0),
                      source: data.placeId ? 'places_autocomplete' : 'manual_entry',
                    });
                  } else {
                    setHomeAddress('');
                    updateFamily.mutate(
                      {
                        homeAddress: null,
                        homeAddressPlaceId: null,
                        homeAddressLat: null,
                        homeAddressLng: null,
                      },
                      {
                        onSuccess: () => {
                          toast.success('Home address removed');
                        },
                        onError: (error) => {
                          console.error('Failed to remove home address:', error);
                          toast.error('Failed to remove home address');
                        },
                      }
                    );
                  }
                }}
                placeholder="For travel time calculations"
                singleAddressMode
              />
              <p className="text-xs text-text-muted mt-1">
                Used to calculate travel time for event reminders
              </p>
            </div>

            {/* Travel Time Preview */}
            <div className="pt-4 border-t border-border-light">
              <h3 className="text-md font-medium text-text-primary mb-3">
                Travel Time Preview
              </h3>
              <TravelTimePreview
                homeAddress={family?.homeAddress || null}
                hasCoordinates={!!(family?.homeAddressLat && family?.homeAddressLng)}
                homeCoordinates={
                  family?.homeAddressLat && family?.homeAddressLng
                    ? { lat: Number(family.homeAddressLat), lng: Number(family.homeAddressLng) }
                    : null
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Timezone
              </label>
              <select
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  updateFamily.mutate({ timezone: e.target.value });
                }}
                className="w-full px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </section>
    </div>
  );

  const renderDisplayTab = () => (
    <div className="space-y-6">
      <DisplaySettingsSection />
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <NotificationsSection />
    </div>
  );

  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      {/* Google Keep Integration */}
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <List className="w-5 h-5" />
          Google Keep Lists
        </h2>

        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-amber-800">Unofficial Integration</div>
            <div className="text-amber-700">
              This uses an unofficial API that may stop working without notice. Your credentials are encrypted and never stored in plain text.
            </div>
          </div>
        </div>

        {/* Connection Status */}
        {keepStatusLoading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-text-muted" />
          </div>
        ) : keepStatus?.connected ? (
          <>
            {/* Connected State */}
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Connected to Google Keep</span>
              </div>
              <button
                onClick={handleDisconnectKeep}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Disconnect
              </button>
            </div>

            {/* Synced Lists */}
            {keepSources && keepSources.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-text-secondary mb-2">Synced Lists</h3>
                <div className="space-y-2">
                  {keepSources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center gap-3 p-2 bg-bg-surface rounded-lg border border-border-light"
                    >
                      <div className="w-8 h-8 rounded flex items-center justify-center bg-yellow-100">
                        <List className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-text-primary truncate">
                          {source.name}
                        </div>
                        <div className="text-xs text-text-muted">
                          {source.lastSynced
                            ? `Synced ${formatDistanceToNow(new Date(source.lastSynced), { addSuffix: true })}`
                            : 'Never synced'}
                          {source.lastSyncError && (
                            <span className="text-red-500 ml-2">Error</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleSyncKeepSource(source.id)}
                        disabled={triggerKeepSync.isPending}
                        className="btn-icon w-7 h-7"
                        title="Sync now"
                      >
                        <RefreshCw className={`w-4 h-4 ${triggerKeepSync.isPending ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleRemoveKeepSync(source.id, source.name)}
                        className="btn-icon w-7 h-7 text-red-500 hover:text-red-600"
                        title="Stop syncing"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import Button */}
            <button
              onClick={() => {
                analytics.keepListsModalOpened();
                setShowKeepListsModal(true);
              }}
              className="btn btn-secondary w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Import from Google Keep
            </button>
          </>
        ) : (
          /* Not Connected State */
          <div className="text-center py-4">
            <List className="w-10 h-10 text-text-muted mx-auto mb-2" />
            <p className="text-text-secondary mb-4">
              Connect Google Keep to sync your lists
            </p>
            <button
              onClick={() => {
                analytics.keepConnectModalOpened();
                setShowKeepConnectModal(true);
              }}
              className="btn btn-primary"
            >
              Connect Google Keep
            </button>
          </div>
        )}
      </section>

      {/* Lists Management */}
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <List className="w-5 h-5" />
          Manage Lists
        </h2>

        {/* Default List Setting */}
        {lists && lists.length > 0 && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border-light">
            <div>
              <div className="font-medium text-text-primary">Default List</div>
              <div className="text-sm text-text-secondary">Opens automatically on Lists page</div>
            </div>
            <select
              className="px-3 py-2 border border-border-default rounded-lg bg-white"
              value={displaySettings?.defaultListId || ''}
              onChange={(e) => {
                const listId = e.target.value || null;
                updateDisplaySettings.mutate({ defaultListId: listId });
                analytics.featureUsed('display_settings_changed', { setting: 'default_list_id', value: listId });
              }}
              disabled={updateDisplaySettings.isPending}
            >
              <option value="">First list</option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>{list.name}</option>
              ))}
            </select>
          </div>
        )}

        {listsListLoading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-text-muted" />
          </div>
        ) : !lists || lists.length === 0 ? (
          <div className="text-center py-4">
            <List className="w-10 h-10 text-text-muted mx-auto mb-2" />
            <p className="text-text-secondary">No lists yet</p>
            <p className="text-sm text-text-muted mt-1">
              Create lists from the Lists page
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {lists.map((list) => {
              const isSynced = keepSources?.some(ks => ks.localListId === list.id);
              return (
                <div
                  key={list.id}
                  className="flex items-center gap-3 p-3 bg-bg-surface-hover rounded-lg border border-border-light"
                >
                  <div
                    className="w-8 h-8 rounded flex items-center justify-center"
                    style={{ backgroundColor: list.color ? `${list.color}40` : '#fe743440' }}
                  >
                    <List className="w-4 h-4" style={{ color: list.color || '#fe7434' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-text-primary truncate">
                      {list.name}
                    </div>
                    <div className="text-xs text-text-muted">
                      {list.itemCount - list.checkedCount} item{list.itemCount - list.checkedCount !== 1 ? 's' : ''} remaining
                      {isSynced && (
                        <span className="ml-2 text-amber-600">Synced with Keep</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleOpenDeleteListModal(list.id, list.name)}
                    className="btn-icon w-8 h-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                    title="Delete list"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );

  const renderAccountTab = () => (
    <div className="space-y-6">
      <KioskSettingsSection />
      <section className="card">
        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <User className="w-5 h-5" />
          Account
        </h2>
        <div className="space-y-4">
          {user && (
            <div className="flex items-center gap-3 p-3 bg-bg-surface-hover rounded-lg">
              <div className="w-10 h-10 rounded-full bg-soft-cyan flex items-center justify-center text-white font-medium">
                {user.name?.substring(0, 2) || user.email?.substring(0, 2) || '?'}
              </div>
              <div>
                {user.name && <div className="font-medium text-text-primary">{user.name}</div>}
                {user.email && <div className="text-sm text-text-secondary">{user.email}</div>}
                <div className="text-xs text-text-muted capitalize">{user.role || 'Member'}</div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 text-red-600 hover:text-red-700 py-2"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </section>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'family':
        return renderFamilyTab();
      case 'display':
        return renderDisplayTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'integrations':
        return renderIntegrationsTab();
      case 'account':
        return renderAccountTab();
      default:
        return renderFamilyTab();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header - hidden on phone (tabs serve as header) */}
      {!isPhone && (
        <div className="bg-bg-surface border-b border-border-default px-6 py-4">
          <h1 className="text-2xl font-semibold text-text-primary">Settings</h1>
        </div>
      )}

      {/* Tab Bar */}
      <div className="bg-bg-surface border-b border-border-default sticky top-0 z-10">
        {isPhone ? (
          /* Mobile: equal-width tabs with icon above label, all fit on screen */
          <nav className="flex" aria-label="Settings tabs">
            {SETTINGS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold border-b-2 transition-colors active:scale-95 ${
                  activeTab === tab.id
                    ? 'border-teal text-teal'
                    : 'border-transparent text-text-muted'
                }`}
                aria-selected={activeTab === tab.id}
                role="tab"
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        ) : (
          /* Desktop: horizontal tabs with icon + label inline */
          <nav className="flex gap-1 overflow-x-auto scrollbar-hide -mb-px px-6" aria-label="Settings tabs">
            {SETTINGS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-soft-cyan text-soft-cyan'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-default'
                }`}
                aria-selected={activeTab === tab.id}
                role="tab"
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Settings Content */}
      <div className={`flex-1 overflow-auto ${isPhone ? 'p-3' : 'p-6'}`}>
        <div className="max-w-3xl">
          {renderTabContent()}
        </div>
      </div>

      {/* ============================================ */}
      {/* Modals (always rendered regardless of tab)   */}
      {/* ============================================ */}

      {/* Member Modal */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h2 className="text-lg font-semibold text-text-primary">
                {editingMember ? 'Edit Member' : 'Add Family Member'}
              </h2>
              <button
                onClick={() => {
                  setShowMemberModal(false);
                  setEditingMember(null);
                  setMemberForm({ name: '', type: 'adult', color: memberColors[0] ?? '#15a2a2' });
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitMember} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
                <input
                  type="text"
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="Enter name"
                  className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Type</label>
                <select
                  value={memberForm.type}
                  onChange={(e) => setMemberForm({ ...memberForm, type: e.target.value as 'adult' | 'teen' | 'child' })}
                  className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                >
                  <option value="adult">Adult</option>
                  <option value="teen">Teen</option>
                  <option value="child">Child</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Color</label>
                <div className="grid grid-cols-8 gap-2">
                  {memberColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setMemberForm({ ...memberForm, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        memberForm.color === color
                          ? 'border-gray-800 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowMemberModal(false);
                    setEditingMember(null);
                    setMemberForm({ name: '', type: 'adult', color: memberColors[0] ?? '#15a2a2' });
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!memberForm.name.trim() || createMember.isPending || updateMember.isPending}
                  className="flex-1 btn btn-primary"
                >
                  {editingMember ? 'Save Changes' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Compass Modal - Separate component */}
      {showAddSourceModal && addSourceType === 'compass' && addSourceMemberId && (
        <AddCompassModal
          memberId={addSourceMemberId}
          memberName={members?.find(m => m.id === addSourceMemberId)?.name || 'Member'}
          onClose={() => {
            setShowAddSourceModal(false);
            setAddSourceType(null);
          }}
        />
      )}

      {/* Add Calendar Source Modal */}
      {showAddSourceModal && addSourceType && addSourceType !== 'compass' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h2 className="text-lg font-semibold text-text-primary">
                {addSourceType === 'google' && 'Add Google Calendar'}
                {addSourceType === 'microsoft' && 'Add Microsoft Calendar'}
                {addSourceType === 'ics' && 'Add ICS Calendar'}
              </h2>
              <button
                onClick={() => {
                  setShowAddSourceModal(false);
                  setAddSourceType(null);
                  setIcsUrl('');
                  setIcsName('');
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Google Calendar Selection */}
              {addSourceType === 'google' && (
                <>
                  {googleLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
                    </div>
                  ) : !googleCalendars?.calendars || googleCalendars.calendars.length === 0 ? (
                    <div className="text-center py-6">
                      <Calendar className="w-10 h-10 text-text-muted mx-auto mb-2" />
                      <p className="text-text-secondary mb-4">No Google calendars available</p>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (addSourceMemberId) {
                            params.set('memberId', addSourceMemberId);
                            params.set('sourceType', 'google');
                          }
                          window.location.href = `/api/v1/connect/google?${params.toString()}`;
                        }}
                        className="btn btn-primary"
                      >
                        Connect Google Account
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Display errors from accounts that failed */}
                      {googleCalendars.errors && googleCalendars.errors.length > 0 && (
                        <div className="mb-4 space-y-2" data-testid="calendar-errors">
                          {googleCalendars.errors.map((error, idx) => (
                            <div
                              key={idx}
                              className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
                            >
                              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-amber-900">{error.accountEmail}</p>
                                <p className="text-xs text-amber-700 mt-0.5">{error.message}</p>
                                {error.needsReconnect && (
                                  <button
                                    onClick={() => {
                                      const params = new URLSearchParams();
                                      if (addSourceMemberId) {
                                        params.set('memberId', addSourceMemberId);
                                        params.set('sourceType', 'google');
                                      }
                                      window.location.href = `/api/v1/connect/google?${params.toString()}`;
                                    }}
                                    className="mt-2 text-xs text-amber-900 underline hover:no-underline"
                                  >
                                    Reconnect now
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {googleCalendars.calendars
                          .filter((calendar) => {
                          // Filter out already connected calendars for this member
                          if (!calendarSources) return true;
                          return !calendarSources.some(
                            (source) => source.externalId === calendar.id && source.type === 'google'
                          );
                        })
                        .map((calendar) => (
                        <button
                          key={calendar.id}
                          onClick={() => handleAddGoogleCalendar(calendar)}
                          disabled={addCalendarSource.isPending}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border border-border-light hover:bg-bg-surface-hover transition-colors text-left"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: calendar.backgroundColor || calendar.color || '#4285F4' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-text-primary truncate">{calendar.name}</div>
                            <div className="text-xs text-text-muted truncate">{calendar.accountEmail}</div>
                          </div>
                          {calendar.primary && (
                            <span className="text-xs px-2 py-0.5 bg-soft-cyan rounded-full shrink-0">Primary</span>
                          )}
                        </button>
                      ))}
                      {googleCalendars.calendars.filter((calendar) => {
                        if (!calendarSources) return true;
                        return !calendarSources.some(
                          (source) => source.externalId === calendar.id && source.type === 'google'
                        );
                      }).length === 0 && (
                        <p className="text-text-muted text-sm text-center py-4">
                          All available calendars are already connected.
                        </p>
                      )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Microsoft Calendar Selection */}
              {addSourceType === 'microsoft' && (
                <>
                  {microsoftLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
                    </div>
                  ) : !microsoftCalendars?.calendars || microsoftCalendars.calendars.length === 0 ? (
                    <div className="text-center py-6">
                      <Calendar className="w-10 h-10 text-text-muted mx-auto mb-2" />
                      <p className="text-text-secondary mb-4">No Microsoft calendars available</p>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (addSourceMemberId) {
                            params.set('memberId', addSourceMemberId);
                            params.set('sourceType', 'microsoft');
                          }
                          window.location.href = `/api/v1/connect/microsoft?${params.toString()}`;
                        }}
                        className="btn btn-primary"
                      >
                        Connect Microsoft Account
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Display errors from accounts that failed */}
                      {microsoftCalendars.errors && microsoftCalendars.errors.length > 0 && (
                        <div className="mb-4 space-y-2" data-testid="calendar-errors">
                          {microsoftCalendars.errors.map((error, idx) => (
                            <div
                              key={idx}
                              className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2"
                            >
                              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-amber-900">{error.accountEmail}</p>
                                <p className="text-xs text-amber-700 mt-0.5">{error.message}</p>
                                {error.needsReconnect && (
                                  <button
                                    onClick={() => {
                                      const params = new URLSearchParams();
                                      if (addSourceMemberId) {
                                        params.set('memberId', addSourceMemberId);
                                        params.set('sourceType', 'microsoft');
                                      }
                                      window.location.href = `/api/v1/connect/microsoft?${params.toString()}`;
                                    }}
                                    className="mt-2 text-xs text-amber-900 underline hover:no-underline"
                                  >
                                    Reconnect now
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {microsoftCalendars.calendars
                          .filter((calendar) => {
                          // Filter out already connected calendars for this member
                          if (!calendarSources) return true;
                          return !calendarSources.some(
                            (source) => source.externalId === calendar.id && source.type === 'microsoft'
                          );
                        })
                        .map((calendar) => (
                        <button
                          key={calendar.id}
                          onClick={() => handleAddMicrosoftCalendar(calendar)}
                          disabled={addCalendarSource.isPending}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border border-border-light hover:bg-bg-surface-hover transition-colors text-left"
                        >
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: calendar.color || '#0078D4' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-text-primary truncate">{calendar.name}</div>
                            <div className="text-xs text-text-muted truncate">{calendar.accountEmail}</div>
                          </div>
                        </button>
                      ))}
                      {microsoftCalendars.calendars.filter((calendar) => {
                        if (!calendarSources) return true;
                        return !calendarSources.some(
                          (source) => source.externalId === calendar.id && source.type === 'microsoft'
                        );
                      }).length === 0 && (
                        <p className="text-text-muted text-sm text-center py-4">
                          All available calendars are already connected.
                        </p>
                      )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ICS URL Input */}
              {addSourceType === 'ics' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Calendar Name
                    </label>
                    <input
                      type="text"
                      value={icsName}
                      onChange={(e) => setIcsName(e.target.value)}
                      placeholder="e.g., Work Calendar"
                      className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      ICS URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={icsUrl}
                      onChange={(e) => setIcsUrl(e.target.value)}
                      placeholder="https://example.com/calendar.ics"
                      className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Paste the public ICS feed URL from your calendar provider
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => {
                        setShowAddSourceModal(false);
                        setAddSourceType(null);
                        setIcsUrl('');
                        setIcsName('');
                      }}
                      className="flex-1 btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddIcsSource}
                      disabled={!icsUrl.trim() || addCalendarSource.isPending}
                      className="flex-1 btn btn-primary"
                    >
                      {addCalendarSource.isPending ? 'Adding...' : 'Add Calendar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keep Connect Modal */}
      {showKeepConnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h2 className="text-lg font-semibold text-text-primary">
                Connect Google Keep
              </h2>
              <button
                onClick={() => {
                  setShowKeepConnectModal(false);
                  setKeepEmail('');
                  setKeepPassword('');
                  setKeepOAuthToken('');
                  setKeepConnectError(null);
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Method toggle (segmented control) */}
            <div className="px-6 pt-4">
              <div className="flex rounded-lg border border-border-default p-1 bg-bg-muted">
                <button
                  type="button"
                  onClick={() => {
                    setKeepConnectMethod('oauth');
                    setKeepConnectError(null);
                  }}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    keepConnectMethod === 'oauth'
                      ? 'bg-bg-surface text-text-primary shadow-sm'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  OAuth Token (recommended)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setKeepConnectMethod('app-password');
                    setKeepConnectError(null);
                  }}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    keepConnectMethod === 'app-password'
                      ? 'bg-bg-surface text-text-primary shadow-sm'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  App Password
                </button>
              </div>
            </div>

            {keepConnectMethod === 'oauth' ? (
            <form onSubmit={handleConnectKeepOAuth} className="p-6 space-y-4">
              {/* Instructions */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium text-blue-800 mb-1">How to get your OAuth Token</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>
                      Go to{' '}
                      <a
                        href="https://accounts.google.com/EmbeddedSetup"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        accounts.google.com/EmbeddedSetup
                      </a>
                    </li>
                    <li>Log into your Google account, click "I agree" if prompted</li>
                    <li>Open browser DevTools (F12) &rarr; Application &rarr; Cookies &rarr; https://accounts.google.com</li>
                    <li>Find the cookie named <code className="px-1 bg-blue-100 rounded">oauth_token</code> and copy its full value</li>
                    <li>Paste it below and click Connect (the token is single-use and expires quickly, so connect right after copying)</li>
                  </ol>
                </div>
              </div>

              {/* Error Message */}
              {keepConnectError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700">{keepConnectError}</p>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Google Email
                </label>
                <input
                  type="email"
                  value={keepEmail}
                  onChange={(e) => setKeepEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                  autoFocus
                  required
                />
              </div>

              {/* OAuth Token */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  OAuth Token
                </label>
                <textarea
                  value={keepOAuthToken}
                  onChange={(e) => setKeepOAuthToken(e.target.value.trim())}
                  placeholder="oauth2_4/0Ad..."
                  rows={3}
                  className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan font-mono text-xs break-all"
                  required
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowKeepConnectModal(false);
                    setKeepEmail('');
                    setKeepOAuthToken('');
                    setKeepConnectError(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!keepEmail.trim() || !keepOAuthToken.trim() || connectKeepOAuth.isPending}
                  className="flex-1 btn btn-primary"
                >
                  {connectKeepOAuth.isPending ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </form>
            ) : (
            <form onSubmit={handleConnectKeep} className="p-6 space-y-4">
              {/* Warning */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium text-amber-800 mb-1">Important Security Information</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>This uses an unofficial API that may break without notice</li>
                    <li>We recommend using a Google App Password</li>
                    <li>Your credentials are transmitted securely and never stored</li>
                  </ul>
                </div>
              </div>

              {/* Error Message */}
              {keepConnectError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
                  <p className="text-sm font-medium text-red-700">{keepConnectError}</p>
                  <ul className="text-xs text-red-600 list-disc list-inside space-y-1">
                    <li>Check <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/security</a> for security alerts to approve</li>
                    <li>Make sure 2-Step Verification is enabled on your Google account</li>
                    <li>Use an App Password, not your regular Google password</li>
                    <li>Generate a <strong>new</strong> App Password at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                    <li>Try the new password immediately after generating it</li>
                  </ul>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Google Email
                </label>
                <input
                  type="email"
                  value={keepEmail}
                  onChange={(e) => setKeepEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="w-full px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                  autoFocus
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  App Password
                </label>
                <div className="relative">
                  <input
                    type={showKeepPassword ? 'text' : 'password'}
                    value={keepPassword}
                    onChange={(e) => {
                      // Strip spaces from app password (Google displays them as "xxxx xxxx xxxx xxxx")
                      const cleaned = e.target.value.replace(/\s/g, '');
                      setKeepPassword(cleaned);
                    }}
                    placeholder="Your Google App Password"
                    className="w-full px-4 py-2 pr-10 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeepPassword(!showKeepPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    tabIndex={-1}
                  >
                    {showKeepPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-soft-cyan hover:underline"
                  >
                    Create an App Password
                  </a>
                  {' '}in your Google Account settings (spaces are removed automatically)
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowKeepConnectModal(false);
                    setKeepEmail('');
                    setKeepPassword('');
                    setKeepConnectError(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!keepEmail.trim() || !keepPassword.trim() || connectKeep.isPending}
                  className="flex-1 btn btn-primary"
                >
                  {connectKeep.isPending ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* Keep Lists Modal */}
      {showKeepListsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h2 className="text-lg font-semibold text-text-primary">
                Import from Google Keep
              </h2>
              <button
                onClick={() => setShowKeepListsModal(false)}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {keepListsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
                </div>
              ) : !keepLists || keepLists.length === 0 ? (
                <div className="text-center py-6">
                  <List className="w-10 h-10 text-text-muted mx-auto mb-2" />
                  <p className="text-text-secondary">No lists found in Google Keep</p>
                  <p className="text-sm text-text-muted mt-1">
                    Create a list in Google Keep to sync it here
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-text-secondary mb-4">
                    Select a list to import and sync with your local lists.
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {keepLists
                      .filter((list) => !list.synced)
                      .map((list) => (
                        <button
                          key={list.id}
                          onClick={() => {
                            handleImportKeepList(list.id);
                            setShowKeepListsModal(false);
                          }}
                          disabled={enableKeepSync.isPending}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border border-border-light hover:bg-bg-surface-hover transition-colors text-left"
                        >
                          <div
                            className="w-8 h-8 rounded flex items-center justify-center"
                            style={{ backgroundColor: list.color || '#FFF9C4' }}
                          >
                            <List className="w-4 h-4 text-yellow-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text-primary truncate">
                              {list.title}
                            </div>
                            <div className="text-xs text-text-muted">
                              {list.item_count} item{list.item_count !== 1 ? 's' : ''}
                              {list.checked_count > 0 && ` (${list.checked_count} checked)`}
                            </div>
                          </div>
                          <Plus className="w-5 h-5 text-text-muted" />
                        </button>
                      ))}
                    {keepLists.filter((list) => !list.synced).length === 0 && (
                      <div className="text-center py-4">
                        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-text-secondary">All lists are already synced!</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete List Confirmation Modal */}
      {showDeleteListModal && listToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
              <h2 className="text-lg font-semibold text-text-primary">
                Delete List
              </h2>
              <button
                onClick={() => {
                  setShowDeleteListModal(false);
                  setListToDelete(null);
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium text-red-800 mb-1">This action cannot be undone</p>
                  <p>
                    Are you sure you want to delete "<strong>{listToDelete.name}</strong>"?
                    All items in this list will be permanently removed.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowDeleteListModal(false);
                    setListToDelete(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteList}
                  disabled={deleteList.isPending}
                  className="flex-1 btn bg-red-600 text-white hover:bg-red-700"
                >
                  {deleteList.isPending ? 'Deleting...' : 'Delete List'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {inviteMemberId && (
        <InviteMemberModal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setInviteMemberId(null);
            setInviteMemberName('');
          }}
          memberId={inviteMemberId}
          memberName={inviteMemberName}
        />
      )}
    </div>
  );
}